import React, { useState } from 'react';
import { X, Building, Smartphone, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { walletService } from '../../services/WalletService';

const RedemptionModal = ({ isOpen, onClose, maxAmount, onSuccess, userId, userType }) => {
    const [amount, setAmount] = useState(maxAmount || 500);
    const [accountType, setAccountType] = useState('vpa');
    const [isLoading, setIsLoading] = useState(false);
    
    // Bank details state
    const [bankName, setBankName] = useState('');
    const [ifsc, setIfsc] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    
    // UPI state
    const [vpa, setVpa] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (amount < 500) {
            toast.error("Minimum redemption amount is ₹500");
            return;
        }
        if (amount > maxAmount) {
            toast.error("Amount exceeds available balance");
            return;
        }

        const data = {
            userId,
            userType,
            amount: Number(amount),
            accountType,
        };

        if (accountType === 'vpa') {
            if (!vpa) return toast.error("Please enter UPI ID");
            data.vpa = vpa;
        } else {
            if (!bankName || !ifsc || !accountNumber) return toast.error("Please fill all bank details");
            data.bankDetails = { name: bankName, ifsc, account_number: accountNumber };
        }

        setIsLoading(true);
        try {
            await walletService.requestRedemption(userType, data);
            toast.success("Redemption request successful!");
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Redemption error:", error);
            toast.error(error.response?.data?.message || "Failed to process redemption");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center p-5 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800">Redeem Funds</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500">
                        <X size={20} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-5 space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Redeem (₹)</label>
                        <input
                            type="number"
                            min="500"
                            max={maxAmount}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-lg font-semibold"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">Available Balance: ₹{maxAmount}</p>
                    </div>

                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Payout Method</label>
                        <div className="grid grid-cols-2 gap-3">
                            <div 
                                onClick={() => setAccountType('vpa')}
                                className={`cursor-pointer flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${accountType === 'vpa' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}
                            >
                                <Smartphone size={18} />
                                <span className="font-medium">UPI</span>
                            </div>
                            <div 
                                onClick={() => setAccountType('bank_account')}
                                className={`cursor-pointer flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${accountType === 'bank_account' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}
                            >
                                <Building size={18} />
                                <span className="font-medium">Bank</span>
                            </div>
                        </div>
                    </div>

                    {accountType === 'vpa' ? (
                        <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID (VPA)</label>
                                <input
                                    type="text"
                                    value={vpa}
                                    onChange={(e) => setVpa(e.target.value)}
                                    placeholder="example@upi"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 animate-in slide-in-from-left-4 duration-300">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
                                <input
                                    type="text"
                                    value={bankName}
                                    onChange={(e) => setBankName(e.target.value)}
                                    placeholder="John Doe"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                                <input
                                    type="text"
                                    value={ifsc}
                                    onChange={(e) => setIfsc(e.target.value)}
                                    placeholder="HDFC0001234"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                                <input
                                    type="text"
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                    placeholder="1234567890"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                />
                            </div>
                        </div>
                    )}

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={isLoading || amount < 500 || amount > maxAmount}
                            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-all focus:ring-4 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin" size={20} />
                                    Processing...
                                </>
                            ) : (
                                `Redeem ₹${amount}`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RedemptionModal;
